'use client';

import type { PropsWithChildren } from 'react';
import { Children, useCallback, useEffect, useState } from 'react';
import { useCarousel, useCarouselDispatch } from './useCarousel';

export const CarouselList = ({ children }: PropsWithChildren) => {
  const { setAtStart, setAtEnd } = useCarouselDispatch();
  const { trackRef } = useCarousel();
  const [visibleIndexes, setVisibleIndexes] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5]));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number.parseInt(entry.target.getAttribute('data-index') || '0', 10);

          if (entry.isIntersecting) {
            setVisibleIndexes((prev) => {
              const newSet = new Set(prev);
              newSet.add(index);
              return newSet;
            });

            // Stop observing this element after first intersect
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '50px' },
    );

    const placeholders = trackRef.current?.querySelectorAll('[data-index]');
    placeholders?.forEach((el, i) => {
      if (i > 5) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [trackRef]);

  const updateEdgeState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 0);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    // unnecessary to include trackRef in deps, but to mach react rules of hooks for react compiler, it is added
  }, [setAtEnd, setAtStart, trackRef]);

  useEffect(() => {
    updateEdgeState();
  }, [updateEdgeState]);

  const items = Children.toArray(children);

  return (
    <div
      ref={trackRef}
      className="relative -mx-1 grid snap-x snap-mandatory scroll-px-1 auto-cols-[calc((100%-(var(--items-visible)-1)*var(--reco-gap))/var(--items-visible))] grid-flow-col gap-[var(--reco-gap)] overflow-x-auto overscroll-x-contain p-0.5 px-1 [--items-visible:2.15] [--reco-gap:0.375rem] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] @xl:[--items-visible:3.5] @4xl:[--items-visible:4.5]"
      role="list"
      aria-label=""
      onScroll={updateEdgeState}
    >
      {items.map((item, index) => (
        // the index is enough as key here and it prevents rerendering as expected because the index order is ensured
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} role="listitem" className="snap-start" data-index={index}>
          {visibleIndexes.has(index) ? (
            <div className="h-full w-full animate-[fade_0.5s_ease-in_forwards]">{item}</div>
          ) : (
            <div className="h-full w-full" />
          )}
        </div>
      ))}
    </div>
  );
};
